import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import UtmDetailModal from "Components/UtmDetailModal";
import Pagination from "Components/Pagination";
import { getUtmStats, getUtmDetail } from "JS/API";

const DETAIL_LIMIT = 10;
const CAMPAIGN_LIMIT = 10;

// 응답이 { meta, data } 로 감싸진 경우 안쪽 data 를 반환
const unwrap = (res) => res?.data?.data ?? res?.data;

const CSV_HEADERS = ["이름", "아이디", "이메일", "가입일"];

const csvCell = (value) => {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (items) => {
  const rows = items.map((i) =>
    [i.user_name, i.user_id, i.email, i.created_at].map(csvCell).join(",")
  );
  return [CSV_HEADERS.join(","), ...rows].join("\r\n");
};

const downloadCsvFile = (csv, filename) => {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// 전환율(%): 방문 0 이면 계산 불가(null)
const convRate = (visits, signups) =>
  visits > 0 ? (signups / visits) * 100 : null;

// 5% 이상 녹색 / 2~5% 주황 / 2% 미만 빨강
const convColor = (rate) => {
  if (rate == null) return "#9ca3af";
  if (rate >= 5) return "#059669";
  if (rate >= 2) return "#d97706";
  return "#dc2626";
};

const styles = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  kpiRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  kpi: {
    flex: 1,
    minWidth: "180px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "20px 24px",
  },
  kpiLabel: {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "6px",
  },
  kpiValue: {
    fontSize: "30px",
    fontWeight: "700",
    color: "#1f2937",
  },
  kpiSub: {
    fontSize: "12px",
    color: "#9ca3af",
    marginTop: "4px",
  },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "28px",
    backgroundColor: "#ffffff",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "20px",
  },
  cardTitleHint: {
    fontSize: "12px",
    fontWeight: "400",
    color: "#9ca3af",
    marginLeft: "8px",
  },
  bars: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
  },
  barLabel: {
    width: "110px",
    color: "#4b5563",
    flexShrink: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barTrack: {
    flex: 1,
    height: "22px",
    backgroundColor: "#f3f4f6",
    borderRadius: "6px",
    overflow: "hidden",
    display: "flex",
  },
  barVisit: {
    height: "100%",
    backgroundColor: "#93c5fd",
  },
  barSignup: {
    height: "100%",
    backgroundColor: "#2563eb",
  },
  barValue: {
    width: "180px",
    textAlign: "right",
    color: "#6b7280",
    fontSize: "13px",
    flexShrink: 0,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    backgroundColor: "#f9fafb",
    color: "#6b7280",
    fontWeight: "600",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  thNum: {
    textAlign: "right",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1f2937",
  },
  tdNum: {
    textAlign: "right",
  },
  row: {
    cursor: "pointer",
  },
  hint: {
    fontSize: "13px",
    color: "#2563eb",
    marginTop: "12px",
  },
  tableFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "14px",
    paddingTop: "14px",
    borderTop: "1px solid #e5e7eb",
  },
  pageInfo: {
    fontSize: "13px",
    color: "#6b7280",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px",
    color: "#9ca3af",
    fontSize: "14px",
  },
  empty: {
    padding: "60px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
};

const UtmStatsTab = ({ filter, periodLabel, onError }) => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const pageParam = parseInt(searchParams.get("page"), 10);
  const campaignPage = pageParam >= 1 ? pageParam : 1;

  const [detailTarget, setDetailTarget] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailDownloading, setDetailDownloading] = useState(false);

  const loadStats = useCallback(
    async (page) => {
      if (!filter) return;
      setIsLoading(true);
      try {
        const res = await getUtmStats(filter, {
          page,
          limit: CAMPAIGN_LIMIT,
        });
        if (typeof res !== "object" || !res?.data) {
          return onError(res);
        }
        setData(unwrap(res));
      } catch (error) {
        onError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [filter, onError]
  );

  useEffect(() => {
    loadStats(campaignPage);
  }, [loadStats, campaignPage]);

  const fetchDetailPage = useCallback(
    async (target, page, limit = DETAIL_LIMIT) => {
      const res = await getUtmDetail(target, filter, { page, limit });
      if (typeof res !== "object" || !res?.data) {
        onError(res);
        return null;
      }
      return unwrap(res);
    },
    [filter, onError]
  );

  const openDetail = useCallback(
    async (campaign) => {
      setDetailTarget(campaign);
      setDetailPage(1);
      setDetailData(null);
      setDetailLoading(true);
      const detail = await fetchDetailPage(campaign, 1);
      if (detail) setDetailData(detail);
      setDetailLoading(false);
    },
    [fetchDetailPage]
  );

  const changeDetailPage = useCallback(
    async (page) => {
      if (!detailTarget) return;
      setDetailPage(page);
      setDetailLoading(true);
      const detail = await fetchDetailPage(detailTarget, page);
      if (detail) setDetailData(detail);
      setDetailLoading(false);
    },
    [detailTarget, fetchDetailPage]
  );

  const downloadDetailCsv = useCallback(async () => {
    if (!detailTarget) return;
    setDetailDownloading(true);
    try {
      const total = detailData?.total || detailTarget.signups || 0;
      const all = await fetchDetailPage(detailTarget, 1, total || 1);
      if (!all?.items?.length) return;
      const csv = buildCsv(all.items);
      downloadCsvFile(
        csv,
        `UTM_${detailTarget.source}_${detailTarget.campaign || "캠페인없음"}.csv`
      );
    } finally {
      setDetailDownloading(false);
    }
  }, [detailTarget, detailData, fetchDetailPage]);

  const closeDetail = useCallback(() => {
    setDetailTarget(null);
    setDetailData(null);
    setDetailPage(1);
  }, []);

  if (isLoading && !data) {
    return <div style={styles.loading}>로딩 중...</div>;
  }

  const summary = data?.summary || {};
  const sources = data?.sources || [];
  const campaigns = data?.campaigns?.items || [];
  const campaignsTotal = data?.campaigns?.total || 0;
  const maxVisits = Math.max(...sources.map((s) => s.visits || 0), 1);
  const signupRatio =
    summary.total_signups > 0
      ? (((summary.signups || 0) / summary.total_signups) * 100).toFixed(1)
      : null;
  const conversion =
    summary.conversion ?? convRate(summary.visits, summary.signups);

  const campaignTotalPages = Math.max(
    1,
    Math.ceil(campaignsTotal / CAMPAIGN_LIMIT)
  );
  if (sources.length === 0 && campaignsTotal === 0) {
    return <div style={styles.empty}>{periodLabel} 집계된 UTM 유입이 없습니다.</div>;
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.kpiRow}>
        <div style={styles.kpi}>
          <div style={styles.kpiLabel}>UTM 방문</div>
          <div style={styles.kpiValue}>
            {(summary.visits || 0).toLocaleString()}
          </div>
        </div>
        <div style={styles.kpi}>
          <div style={styles.kpiLabel}>UTM 경유 가입</div>
          <div style={styles.kpiValue}>
            {(summary.signups || 0).toLocaleString()}
          </div>
          {signupRatio != null && (
            <div style={styles.kpiSub}>전체 가입 중 {signupRatio}%</div>
          )}
        </div>
        <div style={styles.kpi}>
          <div style={styles.kpiLabel}>평균 전환율</div>
          <div style={styles.kpiValue}>
            {conversion != null ? `${Number(conversion).toFixed(1)}%` : "-"}
          </div>
          <div style={styles.kpiSub}>방문 → 가입</div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          소스별 방문 · 가입
          <span style={styles.cardTitleHint}>진한색=가입, 연한색=나머지 방문</span>
        </div>
        <div style={styles.bars}>
          {sources.map((s) => (
            <div key={s.source} style={styles.barRow}>
              <div style={styles.barLabel}>{s.source}</div>
              <div style={styles.barTrack}>
                <div
                  style={{
                    ...styles.barSignup,
                    width: `${(Math.min(s.signups || 0, s.visits || 0) / maxVisits) * 100}%`,
                  }}
                />
                <div
                  style={{
                    ...styles.barVisit,
                    width: `${(Math.max((s.visits || 0) - (s.signups || 0), 0) / maxVisits) * 100}%`,
                  }}
                />
              </div>
              <div style={styles.barValue}>
                {(s.visits || 0).toLocaleString()} 방문 ·{" "}
                {(s.signups || 0).toLocaleString()} 가입
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>캠페인별 상세</div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>소스</th>
              <th style={styles.th}>매체</th>
              <th style={styles.th}>캠페인</th>
              <th style={{ ...styles.th, ...styles.thNum }}>방문</th>
              <th style={{ ...styles.th, ...styles.thNum }}>가입</th>
              <th style={{ ...styles.th, ...styles.thNum }}>전환율</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, idx) => {
              const rate = convRate(c.visits, c.signups);
              return (
                <tr
                  key={`${c.source}-${c.medium}-${c.campaign}-${idx}`}
                  style={styles.row}
                  onClick={() => openDetail(c)}
                >
                  <td style={styles.td}>{c.source}</td>
                  <td style={styles.td}>{c.medium || "-"}</td>
                  <td style={styles.td}>{c.campaign || "(없음)"}</td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>
                    {(c.visits || 0).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, ...styles.tdNum }}>
                    {(c.signups || 0).toLocaleString()}
                  </td>
                  <td
                    style={{
                      ...styles.td,
                      ...styles.tdNum,
                      color: convColor(rate),
                      fontWeight: 700,
                    }}
                  >
                    {rate != null ? `${rate.toFixed(1)}%` : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={styles.hint}>
          행을 클릭하면 해당 캠페인으로 가입한 회원 명단을 볼 수 있습니다.
        </div>
        {campaignsTotal > CAMPAIGN_LIMIT && (
          <div style={styles.tableFooter}>
            <span style={styles.pageInfo}>
              {campaignPage} / {campaignTotalPages} 페이지 · 전체{" "}
              {campaignsTotal.toLocaleString()}개 캠페인
            </span>
            <Pagination
              pageInfo={{ page: campaignPage, totalPage: campaignTotalPages }}
              setPageInfo={() => {}}
              compact
            />
          </div>
        )}
      </div>

      {detailTarget && (
        <UtmDetailModal
          target={detailTarget}
          periodLabel={periodLabel}
          detail={detailData}
          isLoading={detailLoading}
          page={detailPage}
          limit={DETAIL_LIMIT}
          onPageChange={changeDetailPage}
          onDownloadCsv={downloadDetailCsv}
          downloading={detailDownloading}
          onClose={closeDetail}
        />
      )}
    </div>
  );
};

export default UtmStatsTab;
